<script setup>
// Contador del aviso "Un solo equipo" · SOLO super_admin (Victor 2026-06-07).
// Pill flotante con "Confirmados hoy X/Y"; al dar clic abre el desglose por
// sucursal (quién confirmó ✓ con hora y quién falta ⏳). Refresca cada 45s para
// que el número suba "en vivo" sin recargar.
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import AnnouncementAPI from 'dashboard/api/announcement';

const store = useStore();
const currentAccount = computed(() => store.getters.getCurrentAccount || null);
const isSuperAdmin = computed(() => currentAccount.value?.super_admin === true);

const stats = ref(null);
const open = ref(false);
let timer = null;

const confirmed = computed(() => stats.value?.confirmed ?? 0);
const total = computed(() => stats.value?.total ?? 0);
const pending = computed(() => Math.max(0, total.value - confirmed.value));
const pct = computed(() =>
  total.value ? Math.round((confirmed.value / total.value) * 100) : 0
);
const ringOffset = computed(() => {
  const circ = 138; // 2·π·22
  return circ - (circ * pct.value) / 100;
});
// Anillo mínimo del chip flotante (r=14 → circ≈88). Propuesta 2 aprobada por
// Victor 2026-06-09: solo el aro dorado con el número adentro, así no se monta
// encima del ⚙ / IA / toggle en Chat, Leads, Informes ni Ajustes.
const chipRingOffset = computed(() => {
  const circ = 88; // 2·π·14
  return circ - (circ * pct.value) / 100;
});

// Pestaña activa del desglose (Victor 2026-06-09): "confirmados" muestra todo;
// "bloqueados" filtra SOLO los que faltan por confirmar (⏳) y oculta las
// sucursales sin bloqueados, para ver de un vistazo quiénes tienen el sistema
// bloqueado.
const activeTab = ref('confirmados');
const displayBranches = computed(() => {
  const branches = stats.value?.branches || [];
  if (activeTab.value !== 'bloqueados') return branches;
  return branches
    .map(b => ({ ...b, agents: (b.agents || []).filter(a => !a.confirmed) }))
    .filter(b => b.agents.length);
});

async function load() {
  if (!isSuperAdmin.value) return;
  try {
    const { data } = await AnnouncementAPI.stats();
    stats.value = data;
  } catch (e) {
    /* silencioso */
  }
}

function toggle() {
  open.value = !open.value;
  if (open.value) load();
}

function pctBranch(b) {
  return b.total ? Math.round((b.confirmed / b.total) * 100) : 0;
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('es-DO', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Santo_Domingo',
    });
  } catch (e) {
    return '';
  }
}

function initials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase())
    .join('');
}

onMounted(() => {
  if (isSuperAdmin.value) {
    load();
    timer = setInterval(load, 45000);
  }
});
onUnmounted(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <div v-if="isSuperAdmin && stats">
    <!-- Anillo mínimo flotante (Propuesta 2) -->
    <button
      type="button"
      class="ac-chip"
      :title="`Confirmaciones ${confirmed}/${total}`"
      @click="toggle"
    >
      <svg class="ac-ring-svg" width="34" height="34">
        <circle cx="17" cy="17" r="14" stroke="rgba(255,255,255,.08)" stroke-width="3.5" fill="none" />
        <circle
          cx="17" cy="17" r="14" stroke="#c9a35a" stroke-width="3.5" fill="none"
          stroke-dasharray="88" :stroke-dashoffset="chipRingOffset" stroke-linecap="round"
        />
      </svg>
      <span class="ac-ring-num">{{ confirmed }}</span>
    </button>

    <!-- Desglose -->
    <div v-if="open" class="ac-overlay" @click.self="open = false">
      <div class="ac-drill">
        <div class="ac-h">
          <div>
            <h3>Aviso “Un solo equipo”</h3>
            <div class="ac-sub">
              {{ confirmed }} de {{ total }} confirmaron hoy · vigencia 10 jun
              <template v-if="stats.active === false"> · finalizado</template>
            </div>
          </div>
          <div class="ac-ring">
            <svg width="54" height="54">
              <circle cx="27" cy="27" r="22" stroke="rgba(255,255,255,.08)" stroke-width="5" fill="none" />
              <circle
                cx="27" cy="27" r="22" stroke="#c9a35a" stroke-width="5" fill="none"
                stroke-dasharray="138" :stroke-dashoffset="ringOffset" stroke-linecap="round"
              />
            </svg>
            <span class="ac-pct">{{ pct }}%</span>
          </div>
          <button type="button" class="ac-x" @click="open = false">✕</button>
        </div>

        <div class="ac-tabs">
          <button type="button" class="ac-tab" :class="{ on: activeTab === 'confirmados' }" @click="activeTab = 'confirmados'">✓ Confirmados · {{ confirmed }}</button>
          <button type="button" class="ac-tab" :class="{ on: activeTab === 'bloqueados' }" @click="activeTab = 'bloqueados'">⏳ Bloqueados · {{ pending }}</button>
        </div>

        <div class="ac-rows">
          <div v-if="activeTab === 'bloqueados' && !displayBranches.length" class="ac-empty" style="padding: 24px; text-align: center">🎉 Nadie bloqueado · todos confirmaron</div>
          <div v-for="b in displayBranches" :key="b.id || b.name" class="ac-row">
            <div class="ac-row-top">
              <span class="ac-bn">{{ b.name }}</span>
              <span class="ac-st">
                <span class="g">{{ b.confirmed }}</span><span class="sep"> · </span><span class="r">{{ Math.max(0, b.total - b.confirmed) }}</span> falta
              </span>
            </div>
            <div class="ac-bar"><span class="ac-bf" :style="{ width: pctBranch(b) + '%' }" /></div>
            <div class="ac-ags">
              <span
                v-for="ag in b.agents"
                :key="ag.id"
                class="ac-ag"
                :class="ag.confirmed ? 'ok' : 'no'"
              >
                <span class="ac-av">{{ initials(ag.name) }}</span>
                {{ ag.name }}
                <span class="ac-tk">{{ ag.confirmed ? '✓' : '⏳' }}</span>
                <span v-if="ag.confirmed && ag.confirmed_at" class="ac-tm">{{ fmtTime(ag.confirmed_at) }}</span>
              </span>
              <span v-if="!b.agents.length" class="ac-empty">Sin agentes</span>
            </div>
          </div>
        </div>

        <div class="ac-note">
          Los <b>bloqueados (⏳)</b> tienen el sistema bloqueado hasta que den clic
          en Confirmado. El aviso les vuelve a salir cada día hasta el 17 de junio.
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ac-chip {
  position: fixed;
  top: 12px;
  right: 64px;
  z-index: 7000;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  transition: transform 0.16s ease;
}
.ac-chip:hover {
  transform: scale(1.08);
}
.ac-ring-svg {
  transform: rotate(-90deg);
  display: block;
}
.ac-ring-num {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
  color: #34c759;
  line-height: 1;
}

.ac-overlay {
  position: fixed;
  inset: 0;
  z-index: 7001;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.ac-drill {
  width: 100%;
  max-width: 680px;
  border: 1px solid rgba(201, 163, 90, 0.26);
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(19, 17, 20, 0.98), rgba(11, 11, 13, 0.99));
  box-shadow: 0 50px 130px rgba(0, 0, 0, 0.7);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.ac-h {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.ac-h h3 {
  font-size: 15px;
  font-weight: 500;
  color: #f4f4f5;
}
.ac-sub {
  font-size: 11px;
  color: #6c6c70;
  font-weight: 300;
  margin-top: 3px;
}
.ac-ring {
  margin-left: auto;
  position: relative;
  width: 54px;
  height: 54px;
}
.ac-ring svg {
  transform: rotate(-90deg);
}
.ac-pct {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #c9a35a;
}
.ac-x {
  border: 0;
  background: transparent;
  color: #6c6c70;
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
}
.ac-x:hover {
  color: #f4f4f5;
}
.ac-tabs {
  display: flex;
  gap: 8px;
  padding: 13px 22px 0;
}
.ac-tab {
  font-size: 11px;
  color: #9a9a9f;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.025);
  font-weight: 300;
  cursor: pointer;
  font-family: inherit;
  transition: 0.16s;
}
.ac-tab:hover {
  color: #c9a35a;
  border-color: rgba(201, 163, 90, 0.2);
}
.ac-tab.on {
  background: rgba(201, 163, 90, 0.1);
  border-color: rgba(201, 163, 90, 0.26);
  color: #c9a35a;
  font-weight: 600;
}
.ac-rows {
  padding: 14px 22px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  overflow: auto;
}
.ac-row {
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 13px 15px;
  background: rgba(255, 255, 255, 0.025);
}
.ac-row-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 9px;
}
.ac-bn {
  font-size: 12.5px;
  font-weight: 500;
  color: #f4f4f5;
}
.ac-st {
  margin-left: auto;
  font-size: 11px;
  font-weight: 300;
  color: #9a9a9f;
}
.ac-st .g {
  color: #34c759;
  font-weight: 600;
}
.ac-st .r {
  color: #e0a23c;
  font-weight: 600;
}
.ac-st .sep {
  color: #46464a;
}
.ac-bar {
  height: 6px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.07);
  overflow: hidden;
  margin-bottom: 11px;
}
.ac-bf {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #2e8c4a, #34c759);
}
.ac-ags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ac-ag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  font-weight: 300;
  padding: 4px 9px 4px 5px;
  border-radius: 99px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  color: #9a9a9f;
}
.ac-ag.ok {
  border-color: rgba(52, 199, 89, 0.32);
  color: #f4f4f5;
}
.ac-ag.no {
  border-color: rgba(224, 162, 60, 0.3);
}
.ac-av {
  width: 19px;
  height: 19px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 8.5px;
  font-weight: 700;
  background: linear-gradient(135deg, #2a2a2e, #191919);
  color: #9a9a9f;
}
.ac-ag.ok .ac-av {
  background: rgba(52, 199, 89, 0.18);
  color: #7ee29a;
}
.ac-ag.no .ac-av {
  background: rgba(224, 162, 60, 0.15);
  color: #e0a23c;
}
.ac-tk {
  font-size: 9.5px;
}
.ac-ag.ok .ac-tk {
  color: #34c759;
}
.ac-ag.no .ac-tk {
  color: #e0a23c;
}
.ac-tm {
  color: #46464a;
  font-size: 9px;
}
.ac-empty {
  font-size: 10.5px;
  color: #46464a;
  font-style: italic;
}
.ac-note {
  margin: 4px 22px 18px;
  font-size: 10.5px;
  font-weight: 300;
  color: #6c6c70;
  border: 1px solid rgba(224, 162, 60, 0.22);
  background: rgba(224, 162, 60, 0.05);
  border-radius: 10px;
  padding: 10px 13px;
  line-height: 1.55;
}
.ac-note b {
  color: #e0a23c;
  font-weight: 600;
}
</style>
